import type { Item, User } from '@closetly/core';
import { useEffect, useRef, useState } from 'react';
import { APP_NAME } from '../lib/config';
import { ItemEditorProvider, useItemEditor } from '../lib/editor';
import { useItems, useRealtime } from '../lib/queries';
import { navigate, parseRoute, usePath } from '../lib/router';
import { forgetPushOnLogout, resyncPush } from '../lib/push';
import { useOnline } from '../lib/useOnline';
import { BoardDetail } from './BoardDetail';
import { BoardsPage } from './BoardsPage';
import { BrowserButtonSheet } from './BrowserButtonSheet';
import type { SharedLink } from './ItemSheet';
import { NotifySheet } from './NotifySheet';
import { OutfitDetail } from './OutfitDetail';
import { OutfitEditor } from './OutfitEditor';
import { OutfitsPage } from './OutfitsPage';
import { WishlistPage } from './WishlistPage';

interface Props {
  user: User;
  onLogout: () => void;
  /** A link shared into the app (Android share sheet, browser button, iOS shortcut). */
  shared: SharedLink | null;
}

/** Header, navigation and the page for the current path. */
export function Shell({ user, onLogout, shared }: Props) {
  const route = parseRoute(usePath());
  const items = useItems(user.id);
  useRealtime(user.id);
  const online = useOnline();
  const [buttonOpen, setButtonOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);

  // the server may have lost this device, or another account used it
  useEffect(() => {
    resyncPush().catch(() => {});
  }, []);

  async function logout() {
    await forgetPushOnLogout();
    onLogout();
  }

  const tab = route.page === 'wishlist' ? 'wishlist' : route.page.startsWith('board') ? 'boards' : 'outfits';
  const tabs = [
    ['wishlist', 'Teile', '/'],
    ['outfits', 'Outfits', '/outfits'],
    ['boards', 'Boards', '/boards'],
  ] as const;

  return (
    <ItemEditorProvider user={user}>
      <OpenShared shared={shared} />
      <OpenFromNotification items={items.data} />
      <div className={`wrap page-${route.page}`}>
        <header className="top">
          <h1>{APP_NAME}</h1>
          <nav className="seg tabs" aria-label="Bereiche">
            {tabs.map(([key, label, href]) => (
              <a
                key={key}
                href={href}
                aria-current={tab === key ? 'page' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(href);
                }}
              >
                {label}
              </a>
            ))}
          </nav>
        </header>

        {(!online || items.isRefetchError) && (
          <p className="offline" role="status">
            Keine Verbindung. Du siehst den letzten Stand. Änderungen werden gesendet, sobald du wieder online bist.
          </p>
        )}

        {route.page === 'wishlist' && <WishlistPage user={user} />}
        {route.page === 'outfits' && <OutfitsPage user={user} />}
        {route.page === 'outfit' && <OutfitDetail user={user} id={route.id} />}
        {route.page === 'outfit-edit' && <OutfitEditor user={user} id={route.id} />}
        {route.page === 'boards' && <BoardsPage user={user} />}
        {route.page === 'board' && <BoardDetail user={user} id={route.id} />}

        <p className="foot">
          Deine Liste ist gespeichert und nur für dich sichtbar. Angemeldet als {user.email} ·{' '}
          <button type="button" className="linkbtn" onClick={logout}>
            Abmelden
          </button>
          <br />
          <button type="button" className="linkbtn" onClick={() => setButtonOpen(true)}>
            Browser-Knopf für gesperrte Shops
          </button>{' '}
          ·{' '}
          <button type="button" className="linkbtn" onClick={() => setNotifyOpen(true)}>
            Mitteilungen
          </button>
        </p>
      </div>
      {buttonOpen && <BrowserButtonSheet onClose={() => setButtonOpen(false)} />}
      {notifyOpen && <NotifySheet onClose={() => setNotifyOpen(false)} />}
    </ItemEditorProvider>
  );
}

/** Opens the item form once for a link that was shared into the app. */
function OpenShared({ shared }: { shared: SharedLink | null }) {
  const editor = useItemEditor();
  const done = useRef(false);
  useEffect(() => {
    if (shared && !done.current) {
      done.current = true;
      editor.openNew({ shared });
    }
  }, [shared, editor]);
  return null;
}

/** The item a tapped price alert points to: /?teil=<id> on start, or a message from the service worker. */
function itemFromUrl(url: string): string | null {
  return new URL(url, location.origin).searchParams.get('teil');
}

function OpenFromNotification({ items }: { items: Item[] | undefined }) {
  const editor = useItemEditor();
  const [wanted, setWanted] = useState<string | null>(() => {
    const id = itemFromUrl(location.href);
    if (id) history.replaceState(null, '', location.pathname);
    return id;
  });

  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return;
    const onMessage = (e: MessageEvent<{ type?: string; url?: string }>) => {
      if (e.data?.type !== 'open' || !e.data.url) return;
      navigate(new URL(e.data.url, location.origin).pathname);
      setWanted(itemFromUrl(e.data.url));
    };
    sw.addEventListener('message', onMessage);
    return () => sw.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!wanted || !items) return;
    const item = items.find((i) => i.id === wanted);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- consumed once
    setWanted(null);
    if (item) editor.openItem(item);
  }, [wanted, items, editor]);
  return null;
}
